import styled, { css } from 'styled-components';

import { colors, layout, media } from '@prisjakt/ui-base';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import { CardActionArea } from '@prisjakt/ui/card';

import { GoToStoreButton } from '../go-to-store-button';

import { ITableLinkProps } from '../../types';

type SharedProps = {
  $featured?: boolean;
  $forceMobileView?: boolean;
  $newOfferItemExperiment?: boolean;
};

type AlternativeRowProps = {
  $isAlternative?: boolean;
};

type ExperimentProps = {
  $newOfferItemExperiment?: boolean;
};

export const StyledActionArea = styled(CardActionArea)`
  flex: 1 1 auto;
  cursor: pointer;
  overflow: visible;
`;

export const StyledProductNameColumn = styled.div<SharedProps>(
  props => css`
    align-items: center;
    flex: 0 1 auto;
    display: grid;
    max-width: 60%;

    ${props.$forceMobileView &&
      media.mediumUp`
    display: flex;
    max-width: 65%;
  `}

    ${props.$newOfferItemExperiment &&
      media.smallOnly`
        max-width: 100%;
    `}
  `
);

export const StyledProductName = styled(BodySmallText)<
  SharedProps & AlternativeRowProps
>(
  props => css`
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${props.$featured
      ? props.theme.text.primary
      : props.theme.text.tertiary};
    display: none;

    ${props.$isAlternative &&
      css`
        white-space: nowrap;
        display: inline;
      `}

    ${!props.$forceMobileView &&
      media.mediumUp`
      display: inline;
    `}
  `
);

export const StyledTopMiddleRow = styled.div<SharedProps & AlternativeRowProps>(
  props =>
    css`
      display: flex;
      padding: ${layout.measurements.spacing050} 0;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: space-between;
      flex: 1;

      ${!props.$forceMobileView &&
        media.mediumUp`
        padding: ${layout.measurements.spacing050};
        gap: 0;
      `}

      ${props.$isAlternative &&
        css`
          gap: ${layout.measurements.spacing050};
        `}
    `
);

export const StyledMiddleColumn = styled.div<SharedProps & ExperimentProps>(
  props => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    width: 100%;

    ${props.$featured &&
      css`
        &:hover::after {
          box-shadow: 0 2px 10px 0 rgb(0, 0, 0, 0.1), 0 0 0 1px rgb(0, 0, 0, 0);
        }
        ::after {
          transition: box-shadow 200ms ease;
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          border-radius: 8px;
        }
      `}

    ${props.$newOfferItemExperiment &&
      !props.$featured &&
      css`
        width: auto;
        flex: 0 1 auto;
      `}
  `
);

export const StyledEndColumn = styled.div<SharedProps>(
  props => css`
    display: flex;
    align-items: stretch;
    height: 100%;
    padding: ${layout.measurements.spacing050};

    ${!props.$forceMobileView &&
      media.largeUp`
      padding-right: ${layout.measurements.spacing100};
    `}

    ${!props.$forceMobileView &&
      media.smallOnly`
      padding-left: 0;
    `}

    /* Small mobile resolutions, e.g. Galaxy Fold */
    @media screen and (max-width: 280px) {
      padding-right: ${layout.measurements.spacing100};
    }

    ${!props.$featured &&
      css`
        visibility: hidden;
      `}
  `
);

interface LinkProps extends ITableLinkProps {
  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  vertical?: boolean;
}

export const StyledLink = styled.a<LinkProps & ExperimentProps>(
  props => css`

  display: flex;
  flex: 2;
  align-items: center;

  ${!props.featured &&
    css`
      cursor: default;
    `}

  ${props.alignCenter &&
    css`
      justify-content: center;
    `}
  ${props.alignLeft &&
    css`
      justify-content: flex-start;
    `}
  ${props.alignRight &&
    css`
      justify-content: flex-end;
    `}

    &:target {
      outline: ${colors.borderLightGray} dotted 1px;
      outline: -webkit-focus-ring-color auto 1px;
    }

  ${props.$newOfferItemExperiment &&
    css`
      flex: 1 0 auto;
    `}
`
);

export const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const StyledRow = styled.div<ExperimentProps>(
  props => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1 0 auto;
    column-gap: ${layout.measurements.spacing025};

    ${props.$newOfferItemExperiment &&
      media.smallOnly`
      align-items: start;
    `}
  `
);

export const StyledDeliveryBadges = styled(StyledRow)`
  gap: ${layout.measurements.spacing050};
`;

export const StyledExpandButton = styled.div`
  margin-left: auto;
`;

export const StyledCTAWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${layout.measurements.spacing025};
  gap: ${layout.measurements.spacing050};
`;

type CTAProps = {
  isAlternative?: boolean;
};

export const StyledCTAButton = styled(GoToStoreButton)<CTAProps>(
  props => css`
    ${!props.isAlternative &&
      css`
        flex: 1 0 auto;
      `}
  `
);
